import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { invalidate } from '@react-three/fiber';
import { FiEdit3, FiRefreshCw } from 'react-icons/fi';
import { RiDeleteBinLine } from 'react-icons/ri';
import { floorplannerStore } from '../../../../store/floorplannerStore';
import { BONI_CHECKBOX_CLASSNAME } from '../../../../constants/brandColors';
import { BackdropImageType, isBackdropImageType } from '../../../../types/floorplannerTypes';
import {
  isBackdropVisible,
  resolveBackdropOpacity,
} from '../../../../utils/backdropImage';
import { editorStore } from '../../../../store/editorStore';
import { BackdropImageModal } from '../../BackdropImageModal';
import OpacityField from '../components/OpacityField';
import type { PropertyPanelController } from '../types';

const BackdropPreviewImage: React.FC<{ backdropImage: BackdropImageType }> = ({ backdropImage }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let isActive = true;
    const image = new Image();

    image.onload = () => {
      if (!isActive) return;

      const context = canvas.getContext('2d');
      if (!context) return;

      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0);

      if (!backdropImage.backdropAlphaMask) return;

      const maskImage = new Image();
      maskImage.onload = () => {
        if (!isActive) return;

        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = canvas.width;
        maskCanvas.height = canvas.height;
        const maskContext = maskCanvas.getContext('2d', {
          willReadFrequently: true,
        });
        if (!maskContext) return;

        maskContext.drawImage(maskImage, 0, 0, canvas.width, canvas.height);

        try {
          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
          const maskData = maskContext.getImageData(0, 0, canvas.width, canvas.height);

          for (let i = 0; i < imageData.data.length; i += 4) {
            const visible = (maskData.data[i] + maskData.data[i + 1] + maskData.data[i + 2]) / 3 >= 192;
            if (!visible) {
              imageData.data[i] = 255;
              imageData.data[i + 1] = 255;
              imageData.data[i + 2] = 255;
              imageData.data[i + 3] = 0;
            }
          }

          context.putImageData(imageData, 0, 0);
        } catch (error) {
          console.warn('Could not apply backdrop preview mask', error);
        }
      };
      maskImage.src = backdropImage.backdropAlphaMask;
    };

    image.onerror = (error) => {
      console.warn('Could not load backdrop preview image', error);
    };
    if (!backdropImage.backdropImagePath.startsWith('data:')) {
      image.crossOrigin = 'anonymous';
    }
    image.src = backdropImage.backdropImagePath;

    return () => {
      isActive = false;
    };
  }, [backdropImage.backdropImagePath, backdropImage.backdropAlphaMask]);

  return <canvas ref={canvasRef} className="h-full w-full object-contain" aria-hidden="true" />;
};

type BackdropImageSectionProps = Pick<PropertyPanelController, 'backdropModal' | 'setBackdropModal' | 'deleteBackdropImage'>;

const BackdropImageSection: React.FC<BackdropImageSectionProps> = ({ backdropModal, setBackdropModal, deleteBackdropImage }) => {
  const { t } = useTranslation();
  const opacityUndoStartedRef = useRef(false);

  const formatMillimeters = (value?: number) => {
    if (!Number.isFinite(value) || !value || value <= 0) return t('propertyPanel.notSet');
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} mm`;
  };

  const formatPercent = (value?: number) => {
    if (!Number.isFinite(value) || !value || value <= 0) return t('propertyPanel.notSet');
    return `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  };

  return (
    <>
      {editorStore.selections
        .filter((selection) => isBackdropImageType(selection))
        .map((object) => {
          const backdropImage = floorplannerStore.symbolsMap.get(object.id) as BackdropImageType;
          const fullImageWidth = backdropImage?.backdropSize && backdropImage?.backdropRatio ? backdropImage.backdropSize / backdropImage.backdropRatio : undefined;
          return (
            <div key={`backdropImage-${backdropImage?.id}`}>
              <div className="border-b border-gray-300" />
              <div className="text-sm font-normal">
                <div className="h-[40px] pl-3 pr-3 flex items-center justify-between">
                  <div className="text-sm font-medium">{t('propertyPanel.backgroundImage')}</div>
                </div>
                <div className="p-3 items-left py-2 space-y-3">
                  <div className="overflow-hidden rounded border border-[#dedbd2] bg-[#f7f6f1]">
                    <div className="aspect-[4/3] w-full">
                      <BackdropPreviewImage backdropImage={backdropImage} />
                    </div>
                  </div>
                  <div className="rounded border border-[#dedbd2] bg-white p-3">
                    <div className="text-sm font-medium text-gray-900">{t('propertyPanel.referenceMeasurement')}</div>
                    <div className="mt-2 space-y-1 text-xs text-gray-600">
                      <div className="flex justify-between gap-3">
                        <span>{t('propertyPanel.referenceDistance')}</span>
                        <span className="text-right font-medium text-gray-900">{formatMillimeters(backdropImage?.backdropSize)}</span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span>{t('propertyPanel.imageSpan')}</span>
                        <span className="text-right font-medium text-gray-900">{formatPercent(backdropImage?.backdropRatio)}</span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span>{t('propertyPanel.fullImageWidth')}</span>
                        <span className="text-right font-medium text-gray-900">{formatMillimeters(fullImageWidth)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded border border-[#dedbd2] bg-white p-3">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{t('propertyPanel.showBackgroundImage')}</div>
                      <div className="mt-1 text-xs leading-4 text-gray-500">{t('propertyPanel.showBackgroundImageHint')}</div>
                    </div>
                    <input
                      id={`showBackdropImage-${backdropImage.id}`}
                      className={BONI_CHECKBOX_CLASSNAME}
                      type="checkbox"
                      checked={isBackdropVisible(backdropImage)}
                      disabled={floorplannerStore.lockedByOther}
                      onChange={(e) => {
                        floorplannerStore.beginUndoTransaction('backdropVisibility');
                        floorplannerStore.recordSymbolForUndo(backdropImage.id);
                        floorplannerStore.updateSymbolProperty(backdropImage.id, 'visible', e.target.checked);
                        floorplannerStore.recordSymbolChange(backdropImage.id, 'modify');
                        floorplannerStore.pushToUndoStack();
                        floorplannerStore.setDirty();
                        invalidate();
                      }}
                    />
                  </div>
                  <div className="rounded border border-[#dedbd2] bg-white px-3 py-2">
                    <OpacityField
                      inputId={`backdrop-opacity-${backdropImage.id}`}
                      ariaLabel={t('propertyPanel.backgroundImageOpacity')}
                      value={resolveBackdropOpacity(backdropImage)}
                      disabled={floorplannerStore.lockedByOther}
                      onChange={(value) => {
                        if (!opacityUndoStartedRef.current) {
                          floorplannerStore.beginUndoTransaction('backdropOpacity');
                          floorplannerStore.recordSymbolForUndo(backdropImage.id);
                          opacityUndoStartedRef.current = true;
                        }
                        floorplannerStore.updateSymbolProperty(backdropImage.id, 'opacity', value);
                        floorplannerStore.setDirty();
                        invalidate();
                      }}
                      onCommit={() => {
                        if (!opacityUndoStartedRef.current) return;
                        floorplannerStore.recordSymbolChange(backdropImage.id, 'modify');
                        floorplannerStore.pushToUndoStack();
                        opacityUndoStartedRef.current = false;
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded border border-[#dedbd2] bg-white p-3">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{t('propertyPanel.lockMovement')}</div>
                      <div className="mt-1 text-xs leading-4 text-gray-500">{t('propertyPanel.unlockToDrag')}</div>
                    </div>
                    <input
                      id={`lockBackdropImage-${backdropImage.id}`}
                      className={BONI_CHECKBOX_CLASSNAME}
                      type="checkbox"
                      checked={!!backdropImage?.locked}
                      disabled={floorplannerStore.lockedByOther}
                      onChange={(e) => {
                        floorplannerStore.pushToUndoStack();
                        floorplannerStore.updateSymbolProperty(backdropImage.id, 'locked', e.target.checked);
                        floorplannerStore.setDirty();
                        invalidate();
                      }}
                    />
                  </div>
                  <div>
                    <button
                      type="button"
                      className="inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-50"
                      onClick={() =>
                        setBackdropModal({
                          mode: 'recalibrate',
                          backdrop: backdropImage,
                        })
                      }
                    >
                      <FiRefreshCw size={15} />
                      {t('propertyPanel.resetReference')}
                    </button>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-50"
                      onClick={() =>
                        setBackdropModal({
                          mode: 'erase',
                          backdrop: backdropImage,
                        })
                      }
                    >
                      <FiEdit3 size={15} />
                      {t('propertyPanel.editImage')}
                    </button>
                  </div>
                  <button type="button" className="inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50" onClick={() => deleteBackdropImage(backdropImage)}>
                    <RiDeleteBinLine size={15} />
                    {t('common.remove')}
                  </button>
                </div>
                <div className="border-b border-gray-300" />
              </div>
            </div>
          );
        })}
      {backdropModal && <BackdropImageModal isOpen={true} mode={backdropModal.mode} backdrop={backdropModal.backdrop} onClose={() => setBackdropModal(null)} />}
    </>
  );
};

export default BackdropImageSection;
